<template>
    <v-container class="py-8" fluid>
        <v-row justify="center">
            <v-col cols="12" md="10" lg="8">
                <v-card elevation="10" class="pa-6">
                    <v-card-title class="d-flex align-center justify-space-between">
                        <span class="headline font-weight-bold">Payment Options</span>
                        <v-btn color="primary" @click="openCreateDialog" large>
                            <v-icon left>mdi-plus</v-icon>
                            New Payment Option
                        </v-btn>
                    </v-card-title>
                    <v-divider class="mb-4" />
                    <v-data-table :headers="headers" :items="paymentOptions" :loading="loading" class="elevation-1"
                        :search="search" item-key="id" disable-sort
                        :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }" mobile-breakpoint="600">
                        <template v-slot:top>
                            <v-text-field v-model="search" append-icon="mdi-magnify" label="Search Payment Options"
                                class="mb-4" clearable hide-details />
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-btn icon color="primary" @click="openEditDialog(item)">
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon color="error" @click="confirmDelete(item)">
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </template>
                        <template v-slot:no-data>
                            <v-alert type="info" border="left" colored-border>
                                No payment options found.
                            </v-alert>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <!-- Create/Edit Dialog -->
        <v-dialog v-model="dialog" max-width="600px" persistent>
            <v-card>
                <v-card-title class="headline font-weight-bold">
                    {{
                        dialogMode === "edit"
                            ? "Edit Payment Option"
                            : "Create Payment Option"
                    }}
                </v-card-title>
                <v-card-text>
                    <v-form ref="form" v-model="formValid" lazy-validation>
                        <v-text-field v-model="form.description" :rules="rules.description" label="Description" required
                            clearable placeholder="e.g. M-Pesa, Bank Transfer, etc." />
                        <v-text-field v-model="form.code" :rules="rules.code" label="Code" required clearable
                            persistent-hint
                            hint="This is the name of targeted payment option component in the paygate package"
                            placeholder="e.g. Mpesa, Cheque, Ecitizen" />
                        <v-text-field v-model="form.ref" :rules="rules.ref" label="Reference (Ref)" required clearable
                            placeholder="e.g. ecitizen, mpesa, cheque" />
                        <v-text-field v-model.number="form.checkoutCode" :rules="rules.checkoutCode"
                            label="Checkout Code" required clearable type="number" placeholder="e.g. 1"
                            hint="Numeric code used for checkout. Must be an integer." persistent-hint />
                        <v-text-field v-model.number="form.priority" :rules="rules.priority" label="Priority" required
                            clearable type="number" placeholder="e.g. 1 (highest), 2, 3..." persistent-hint
                            hint="Determines display order. Lower is higher priority. Must be an integer." />
                        <v-checkbox v-model="form.isConfirmPayment">
                            <template v-slot:label>
                                <div>
                                    Is confirm payment option
                                </div>
                            </template>
                        </v-checkbox>
                    </v-form>
                    <v-alert v-if="formError" type="error" class="mt-2" dense>{{
                        formError
                    }}</v-alert>
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn text @click="closeDialog">Cancel</v-btn>
                    <v-btn color="primary" :loading="formLoading" :disabled="!formValid || formLoading"
                        @click="submitForm">
                        {{ dialogMode === "edit" ? "Update" : "Create" }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="420px">
            <v-card>
                <v-card-title class="headline">Confirm Delete</v-card-title>
                <v-card-text>
                    Are you sure you want to delete
                    <b>{{ deleteTarget && deleteTarget.description }}</b> ({{ deleteTarget && deleteTarget.code }})?
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn text @click="deleteDialog = false">Cancel</v-btn>
                    <v-btn color="error" :loading="deleteLoading" @click="deletePaymentOption">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
export default {
    name: "AdminPaymentOptionsList",
    data() {
        return {
            loading: false,
            search: "",
            dialog: false,
            dialogMode: "create", // or 'edit'
            form: {
                description: "",
                code: "",
                ref: "",
                checkoutCode: null,
                priority: null,
                isConfirmPayment: false,
            },
            formValid: false,
            formLoading: false,
            formError: "",
            deleteDialog: false,
            deleteTarget: null,
            deleteLoading: false,
            headers: [
                { text: "Description", value: "description", align: "start" },
                { text: "Code", value: "code" },
                { text: "Reference", value: "ref" },
                { text: "Checkout Code", value: "checkoutCode" },
                { text: "Priority", value: "priority" },
                {
                    text: "Actions",
                    value: "actions",
                    sortable: false,
                    align: "center",
                    width: 120,
                },
            ],
            rules: {
                description: [
                    (v) => !!v || "Description is required.",
                    (v) => (v && v.length <= 255) || "Max 255 characters.",
                ],
                code: [
                    (v) => !!v || "Code is required.",
                    (v) => (v && v.length <= 255) || "Max 255 characters.",
                ],
                ref: [
                    (v) => !!v || "Reference is required.",
                    (v) => (v && v.length <= 255) || "Max 255 characters.",
                ],
                checkoutCode: [
                    (v) => !!v || "Checkout Code is required.",
                    (v) => Number.isInteger(Number(v)) || "Must be an integer.",
                ],
                priority: [
                    (v) => !!v || "Priority is required.",
                    (v) => Number.isInteger(Number(v)) || "Must be an integer.",
                ],
            },
        };
    },
    created() {
        this.$store.dispatch("Admin/fetchPaymentOptions");
    },
    computed: {
        paymentOptions: {
            get() {
                return this.$store.getters["Admin/adminGetters"]("paymentOptions");
            }
        }
    },
    methods: {
        async fetchPaymentOptions() {
            this.loading = true;
            try {
                const res = await this.$axios.get("/admin/payment-options");
                this.paymentOptions = res.data.data || [];
            } catch (e) {
                this.$emit("error", "Failed to fetch payment options.");
            } finally {
                this.loading = false;
            }
        },
        openCreateDialog() {
            this.dialogMode = "create";
            this.form = {
                description: "",
                code: "",
                ref: "",
                checkoutCode: null,
                priority: null,
            };
            this.formError = "";
            this.dialog = true;
        },
        openEditDialog(item) {
            this.dialogMode = "edit";
            this.form = { ...item };
            this.formError = "";
            this.dialog = true;
        },
        closeDialog() {
            this.dialog = false;
            this.formLoading = false;
            this.formError = "";
        },
        async submitForm() {
            if (!this.$refs.form.validate()) return;
            this.formLoading = true;
            this.formError = "";
            try {
                if (this.dialogMode === "create") {
                    this.$store.dispatch("Admin/createPaymentOption", this.form);
                } else {
                    this.$store.dispatch("Admin/updatePaymentOption", this.form);
                }
                this.dialog = false;
            } catch (e) {
                this.formError =
                    e.response?.data?.message || "Failed to save payment option.";
            } finally {
                this.formLoading = false;
            }
        },
        confirmDelete(item) {
            this.deleteTarget = item;
            this.deleteDialog = true;
        },
        async deletePaymentOption() {
            if (!this.deleteTarget) return;
            this.deleteLoading = true;
            try {
                this.$store.dispatch("Admin/deletePaymentOption", this.deleteTarget);
            } catch (e) {
                this.$emit(
                    "error",
                    e.response?.data?.message || "Failed to delete payment option."
                );
            } finally {
                this.deleteLoading = false;
                this.deleteDialog = false;
                this.deleteTarget = null;
            }
        },
    },
};
</script>

<style scoped>
.v-card {
    border-radius: 18px;
    box-shadow: 0 6px 32px 0 rgba(60, 60, 60, 0.12);
}

.v-card-title {
    color: #2d3748;
}

.headline {
    color: #2d3748;
}

.v-btn {
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.v-data-table {
    background: #fff;
    border-radius: 12px;
}

@media (max-width: 600px) {
    .v-card {
        padding: 8px !important;
    }

    .v-card-title {
        font-size: 1.1rem;
        padding: 12px 8px 8px 8px !important;
    }
}
</style>
